import type { ProductionHistory, ProductionHistoryPoint } from "@/features/devices/types";
import { formatTag, parseTag } from "@/features/machines/addressing";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

/**
 * Estado de leitura do cartão de produção, traduzido para humano:
 *  - `producing`: o último bucket teve movimento (ritmo/produção > 0);
 *  - `stopped`: leitura fresca, mas máquina parada agora;
 *  - `stale`: sem leitura recente (a linha de produção pode estar correndo,
 *    mas o gateway não publicou — não inventamos);
 *  - `empty`: a janela não tem amostras.
 */
export type ProductionRhythmState = "producing" | "stopped" | "stale" | "empty";

export interface ProductionRhythm {
  state: ProductionRhythmState;
  /** Média das leituras positivas de v, na unidade declarada de v (sem conversão). */
  avgRate: number | null;
}

/** A unidade do acumulado não prova a base temporal de v: m³ pode ter vazão m³/h. */
export function declaredProductionRateUnit(
  source: string,
  kinds: Readonly<Record<string, GhmSignalKind>>,
  units: Readonly<Record<string, string | null | undefined>>,
): string | undefined {
  const address = parseTag(source);
  if (address?.kind !== "real" || address.signal !== "acc") return undefined;
  const rateSource = formatTag({ ...address, signal: "v" });
  if (kinds[rateSource] !== "rate") return undefined;
  return units[rateSource]?.trim() || undefined;
}

/** Um bucket "tem movimento" quando rodou (vLast≠0) ou produziu (delta>0). */
function bucketHasMovement(point: ProductionHistoryPoint): boolean {
  const rate = point.vLast ?? point.vAvg ?? 0;
  return rate !== 0 || point.delta > 0 || point.activeSeconds > 0;
}

/**
 * Resume o "ritmo" da janela para a linha de significado do cartão. Só usa o
 * próprio histórico de produção — não recalcula disponibilidade (isso é do
 * widget de disponibilidade). Honesto: se o dado está velho, vira `stale` em
 * vez de afirmar parada/produção.
 */
export function summarizeProductionRhythm(
  history: ProductionHistory,
  isFresh: boolean,
): ProductionRhythm {
  const points = history.points;
  if (points.length === 0) {
    return { state: isFresh ? "empty" : "stale", avgRate: null };
  }

  const rates = points
    .map((point) => point.vAvg ?? point.vLast)
    .filter((rate): rate is number => typeof rate === "number" && Number.isFinite(rate) && rate > 0);
  const avgRate =
    rates.length > 0 ? rates.reduce((sum, rate) => sum + rate, 0) / rates.length : null;

  if (!isFresh) {
    return { state: "stale", avgRate };
  }

  const last = points[points.length - 1];
  return { state: bucketHasMovement(last) ? "producing" : "stopped", avgRate };
}
